/**
 * Hook to handle message sending logic
 * 
 * Uses Backend Proxy approach for file uploads:
 * 1. Convert file to base64
 * 2. Send to Firebase Function as JSON
 * 3. Function uploads to Storage and returns downloadUrl
 * 4. Save message with downloadUrl to Firestore
 */

import { useCallback } from 'react'
import { collection, doc, setDoc, Timestamp } from 'firebase/firestore'
import type { Firestore } from 'firebase/firestore'
import type { IMessage } from '../../types'
import { generateMessageId } from '../utils/messageUtils'
import { getFileIcon, getMessageTypeFromFile, fileToBase64 } from '../utils/fileUtils'
import { ensureChatRoomExists } from '../utils/chatRoomUtils'
import type { UseFileUploadReturn } from './useFileUpload'
import { getUploadFunctionUrl, type FirebaseProjectKey } from '../../firebase'

export interface UseMessageSenderReturn {
    sendMessage: () => Promise<void>
}

interface UploadResponse {
    success: boolean
    downloadUrl?: string
    filePath?: string
    error?: string
}

/**
 * Upload file via Backend Proxy (Base64 JSON)
 */
async function uploadFileViaProxy(
    file: File,
    chatRoomId: string,
    clientId: string,
    uploadFunctionUrl: string
): Promise<{ downloadUrl: string; filePath: string }> {
    // Convert file to base64
    const base64Data = await fileToBase64(file)

    // Send to function as JSON
    const response = await fetch(uploadFunctionUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            clientId,
            chatRoomId,
            mediaFileName: file.name,
            mimeType: file.type,
            mediaType: getMessageTypeFromFile(file),
            mediaData: base64Data,
        }),
    })

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Upload failed: ${response.status}`)
    }

    const data: UploadResponse = await response.json()

    if (!data.success || !data.downloadUrl) {
        throw new Error(data.error || 'Upload failed')
    }

    return {
        downloadUrl: data.downloadUrl,
        filePath: data.filePath!,
    }
}

export function useMessageSender(
    firestore: Firestore | null,
    chatRoomId: string,
    clientId: string,
    userId: string,
    visitorId: string,
    visitorName: string,
    inputValue: string,
    fileUpload: UseFileUploadReturn,
    isPaused: boolean,
    setIsSending: (value: boolean) => void,
    setInputValue: (value: string) => void,
    setIsTyping: (value: boolean) => void,
    setFileError?: (error: string | null) => void,
    firebaseProjectKey?: FirebaseProjectKey,
    apiKey?: string
): UseMessageSenderReturn {
    const sendMessage = useCallback(async () => {
        const text = inputValue.trim()
        const hasFile = fileUpload.selectedFile !== null
        const hasText = text.length > 0

        if ((!hasText && !hasFile) || !clientId || !visitorId || !firestore) {
            return
        }

        setIsSending(true)

        try {
            const now = Timestamp.now()
            const messageId = generateMessageId('user')
            let mimeType: string | undefined
            let messageType: string = 'chat'
            let downloadUrl: string | undefined
            let mediaFileName: string | undefined
            let fileSize: number | undefined

            // Upload file via Backend Proxy if selected
            if (fileUpload.selectedFile) {
                messageType = getMessageTypeFromFile(fileUpload.selectedFile)
                mimeType = fileUpload.selectedFile.type
                mediaFileName = fileUpload.selectedFile.name
                fileSize = fileUpload.selectedFile.size

                // Get upload function URL for the current project
                const uploadFunctionUrl = firebaseProjectKey
                    ? getUploadFunctionUrl(firebaseProjectKey)
                    : getUploadFunctionUrl('1') // Default to staging

                if (!uploadFunctionUrl) {
                    throw new Error('Upload function URL not configured')
                }

                try {
                    console.log('[Chat Widget] Uploading file via Backend Proxy...')
                    console.log('[Chat Widget] Using function URL:', uploadFunctionUrl)
                    const uploadResult = await uploadFileViaProxy(
                        fileUpload.selectedFile,
                        chatRoomId,
                        clientId,
                        uploadFunctionUrl
                    )
                    downloadUrl = uploadResult.downloadUrl
                    console.log('[Chat Widget] File uploaded successfully:', downloadUrl)
                } catch (error: unknown) {
                    console.error('[Chat Widget] File upload error:', error)
                    const errorMsg = error instanceof Error ? error.message : 'Failed to upload file. Please try again.'
                    setFileError?.(errorMsg)
                    throw error
                }
            }

            // Get last message preview text
            const getLastMessagePreview = (): string => {
                if (hasText) return text
                if (hasFile) {
                    const icon = getFileIcon(mimeType, messageType)
                    const fileName = mediaFileName || 'File'
                    return `${icon} ${fileName}`
                }
                return ''
            }

            // Ensure chatroom exists before sending message (lazy creation)
            if (apiKey) {
                await ensureChatRoomExists({
                    firestore,
                    chatRoomId,
                    clientId,
                    visitorId,
                    visitorName,
                    apiKey,
                })
            }

            // Create message record with downloadUrl (no base64 in Firestore)
            const userRecord: IMessage & { mediaFileName?: string; fileSize?: number } = {
                id: messageId,
                chatRoomId,
                clientId,
                userId, // User ID for flow lookup
                body: text || '', // Empty string if no text (file only)
                from: visitorId,
                to: clientId,
                fromMe: false,
                fromAI: false,
                type: messageType,
                name: visitorName,
                remoteJid: visitorId,
                object: 'chat_widget',
                date: now,
                createdBy: clientId,
                // Conditionally include caption only if text is provided with file
                ...(hasText && hasFile && { caption: text }),
                // Conditionally include mimeType only if file exists
                ...(mimeType && { mimeType }),
                // Include downloadUrl if file was uploaded
                ...(downloadUrl && {
                    downloadUrl,
                    mediaFileName,
                    fileSize,
                }),
            }

            const messagesCollection = collection(firestore, 'widgetChatRooms', chatRoomId, 'messages')

            await Promise.all([
                setDoc(doc(messagesCollection, messageId), userRecord),
                setDoc(
                    doc(firestore, 'widgetChatRooms', chatRoomId),
                    {
                        id: chatRoomId,
                        chatRoomId,
                        date: now,
                        lastMessage: getLastMessagePreview(),
                        lastMessageType: messageType,
                        // Don't set aiStatus here - let the function handle it
                    },
                    { merge: true },
                ),
            ])

            // Reset form
            setInputValue('')
            fileUpload.resetFileState()

            // Only show typing indicator if AI is not paused
            if (!isPaused) {
                setIsTyping(true)
            }
        } catch (error: unknown) {
            console.error('[Chat Widget] Failed to send message', error)
            setIsTyping(false)
            // If error message wasn't already set, set a generic one
            if (!fileUpload.fileError && setFileError) {
                const errorMsg = error instanceof Error ? error.message : 'Failed to send message. Please try again.'
                setFileError(errorMsg)
            }
        } finally {
            setIsSending(false)
        }
    }, [
        chatRoomId,
        clientId,
        userId,
        visitorId,
        visitorName,
        inputValue,
        fileUpload,
        isPaused,
        firestore,
        setIsSending,
        setInputValue,
        setIsTyping,
        setFileError,
        firebaseProjectKey,
        apiKey,
    ])

    return {
        sendMessage,
    }
}